import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Flex,
    FlexProps,
    MantineSize,
    Select,
    Text,
} from "@mantine/core";
import {
    IconChevronLeft,
    IconChevronRight,
    IconChevronsLeft,
    IconChevronsRight,
} from "@tabler/icons-react";

import { Pagination as PaginationHook } from "../../hooks/pagination";
import { PaginationItem, PaginationItemProps } from "./item";

const parseLocalItems = (
    localTotal: number,
    offset: number,
    limit: number,
    setOffset: (offset: number) => void,
): {
    items: PaginationItemProps[];
    hasMoreNextPages: boolean;
    lastCursor: number;
} => {
    const items: PaginationItemProps[] = [];

    let hasMorePreviousPages = false;
    for (let cursor = offset - limit; cursor >= 0; cursor -= limit) {
        if (items.length >= 3) {
            hasMorePreviousPages = true;
            break;
        }

        items.unshift({
            content: String(Math.ceil(cursor / limit) + 1),
            onClick: () =>
                setOffset(cursor),
        });
    }

    if (hasMorePreviousPages) {
        items.unshift({ content: "..." });
    }

    let nextPagesCount = 0;
    let hasMoreNextPages = false;
    let lastCursor: number;
    for (let cursor = offset; cursor < localTotal; cursor += limit) {
        if (nextPagesCount >= 4) {
            hasMoreNextPages = true;
            break;
        }

        items.push({
            content: String(Math.ceil(cursor / limit) + 1),
            isActive: cursor === offset,
            onClick: () =>
                setOffset(cursor),
        });

        nextPagesCount += 1;
        lastCursor = cursor;
    }

    if (hasMoreNextPages) {
        items.push({ content: "..." });
    }

    return { items, hasMoreNextPages, lastCursor };
};

const parseRemoteItems = (
    localTotal: number,
    remoteTotal: number,
    offset: number,
    limit: number,
    setOffset: (offset: number) => void,
): PaginationItemProps[] => {
    const { items, hasMoreNextPages, lastCursor } = parseLocalItems(localTotal, offset, limit, setOffset);

    if (!hasMoreNextPages && lastCursor < remoteTotal - limit) {
        items.push({ content: "..." });
    }

    return items;
};

export interface PaginationProps extends FlexProps, PaginationHook {
    size?: MantineSize;
}

export const Pagination = ({
    limits,
    mode,
    localTotal,
    remoteTotal,
    offset,
    limit,
    page,
    // searchParams,
    // setMode,
    // setLocalTotal,
    // setRemoteTotal,
    setOffset,
    setLimit,
    // setPage,
    previousPage,
    nextPage,
    // size,
    ...rest
}: PaginationProps) => {
    const { t } = useTranslation(["common"]);

    const { items, pagesTotal } = useMemo(() => {
        if (mode === "remote") {
            return {
                items: parseRemoteItems(localTotal, remoteTotal, offset, limit, setOffset),
                pagesTotal: Math.ceil(remoteTotal / limit),
            };
        }

        return {
            items: parseLocalItems(localTotal, offset, limit, setOffset).items,
            pagesTotal: Math.ceil(localTotal / limit),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, localTotal, remoteTotal, limit, offset]);

    const hasNextPage = useMemo(() => {
        return page < pagesTotal;
    }, [page, pagesTotal]);

    return (
        <Flex
            {...rest}
            align="center"
            justify="space-between"
        >
            <Box style={{ flex: 1 }} />
            <Flex gap={5} align="center">
                <PaginationItem
                    content={<IconChevronsLeft size={20} />}
                    isDisabled={offset <= 0}
                    onClick={() =>
                        setOffset(0)}
                    isEdge
                />
                <PaginationItem
                    mr="md"
                    content={<IconChevronLeft size={20} />}
                    isDisabled={offset <= 0}
                    onClick={previousPage}
                    isEdge
                />
                {items.map((item, index) => (
                    <PaginationItem
                        key={`${index}`}
                        {...item}
                    />
                ))}
                <PaginationItem
                    ml="md"
                    content={<IconChevronRight size={20} />}
                    isDisabled={!hasNextPage}
                    onClick={nextPage}
                    isEdge
                />
                {mode === "local" && (
                    <PaginationItem
                        content={<IconChevronsRight size={20} />}
                        isDisabled={!hasNextPage}
                        onClick={() =>
                            setOffset((pagesTotal * limit) - limit)}
                        isEdge
                    />
                )}
            </Flex>
            <Flex style={{ flex: 1 }} align="center" justify="end">
                <Text size={14}>
                    {t("pagesize")}
                </Text>
                <Select
                    size="sm"
                    color="gray"
                    maxDropdownHeight={500}
                    value={String(limit)}
                    data={limits.map(limit => String(limit))}
                    sx={{
                        "& .mantine-Select-input": {
                            backgroundColor: "transparent",
                            border: "none",
                            fontWeight: 600,
                            width: 100,
                        },
                    }}
                    onChange={limit =>
                        setLimit(Number(limit))}
                />
            </Flex>
        </Flex>
    );
};
